import { useState, type FormEvent } from 'react';
import { Download, CheckCircle2 } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';

/**
 * GuideEmailGate — mini-barrière courriel pour le téléchargement du guide.
 *
 * Courriel + case Loi 25 → POST /api/guide (D1 + GHL origine "Guide téléchargé")
 * → le PDF se télécharge automatiquement. Friction minimale, conforme,
 * et l'opt-in alimente le workflow infolettre GHL.
 *
 * Utilisé dans le popup de sortie + /ressources.
 */
const PDF_URL = '/guides/amg-quebec-2026.pdf';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function GuideEmailGate({ onDone }: { onDone?: () => void }) {
  const { lang } = useLang();
  const text = ta(t.guide_gate, lang);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  function triggerDownload() {
    const a = document.createElement('a');
    a.href = PDF_URL;
    a.download = 'Guide-AMG-Quebec-2026.pdf';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get('email') ?? '').trim();
    if (!EMAIL_RE.test(email)) {
      setError(text.err_email);
      return;
    }
    if (!fd.get('consent_loi25')) {
      setError(text.err_consent);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch('/api/guide', { method: 'POST', body: fd });
      const data = (await res.json().catch(() => ({ ok: false }))) as { ok?: boolean };
      if (res.ok && data.ok) {
        setSubmitting(false);
        setDone(true);
        triggerDownload();
        onDone?.();
      } else {
        setSubmitting(false);
        setError(text.err_generic);
      }
    } catch {
      setSubmitting(false);
      setError(text.err_generic);
    }
  }

  if (done) {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <CheckCircle2 className="h-8 w-8 text-secondary" aria-hidden="true" />
        <p className="font-sans text-sm text-foreground">{text.success}</p>
        <a href={PDF_URL} download className="font-sans text-xs text-primary underline hover:opacity-80">
          {text.success_link}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-3">
      {/* Honeypot anti-bot (même champ que le form principal) */}
      <input
        type="text"
        name="contact_check_url"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="new-password"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        defaultValue=""
      />
      <label htmlFor="guide-gate-email" className="sr-only">
        {text.email_placeholder}
      </label>
      <input
        id="guide-gate-email"
        name="email"
        type="email"
        required
        maxLength={100}
        placeholder={text.email_placeholder}
        aria-invalid={error ? true : undefined}
        className="w-full rounded-md border border-border bg-background px-4 py-3 font-sans text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-0"
      />
      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          name="consent_loi25"
          value="on"
          required
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
        />
        <span className="font-sans text-xs leading-snug text-muted-foreground">{text.consent}</span>
      </label>
      {error && (
        <p role="alert" className="font-sans text-xs text-primary">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        data-cursor="cta"
        className="shadow-warm-soft hover:shadow-warm-md inline-flex w-full items-center justify-center gap-2.5 rounded-md bg-primary px-6 py-3.5 font-sans text-xs font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60"
      >
        {submitting ? text.submitting : text.cta}
        {!submitting && <Download className="h-4 w-4" strokeWidth={2} />}
      </button>
    </form>
  );
}
